import axios, {
  type AxiosInstance,
  type AxiosError,
  type InternalAxiosRequestConfig,
} from "axios";

/**
 * Project-wide axios instance.
 *
 * - Adds the `Authorization: Bearer <token>` header from local storage.
 * - Adds the `X-Addy-Tenant-Id` header so the backend tenant filter picks it up.
 * - Surfaces RFC 7807 problem+json bodies as `{ status, code, title, detail, properties }`
 *   via the `apiError` helper, so UI components don't need to know about
 *   axios internals.
 *
 * Real auth lands in M23 (`/api/v1/auth/login`); for now the access token may
 * be {@code null} which means the request goes out anonymous. The backend
 * 401-handler in the UI redirects to `/login` on auth failure.
 */

const TENANT_HEADER = "X-Addy-Tenant-Id";

/**
 * `X-Addy-Source` — where the operation came from, for HIS's audit log (#19239).
 *
 * HIS stamps every audit row with a source and keeps a deliberate fallback: a request with no
 * header is recorded as `api`, which is honest ("an API call that did not declare a source") but
 * useless for the question the log exists to answer — "who did this, from where".
 *
 * **Derived from the route, not passed per call.** The alternative was an extra argument at every
 * call site, and that fails in the one way that matters: the places that forget are invisible, and
 * they are recorded as `api` without anything going red. The route already *is* the answer — the
 * user is on the reception desk or in an appointment's detail screen — so reading it once in the
 * interceptor makes forgetting impossible.
 *
 * The vocabulary is closed and owned by `AuditSource` in `libs/platform-infra`; a value outside it
 * is recorded as `api` with a WARN. Anything not listed here sends no header at all, which is the
 * same outcome and one less thing to keep in sync.
 */
const SOURCE_HEADER = "X-Addy-Source";

/**
 * Route → audit source. Exported for its test: this mapping is the whole feature, and it is a pure
 * function of the path precisely so it can be checked without a browser.
 */
export function auditSourceForPath(pathname: string): string | null {
  // Reception desk — the queue screen the front desk works from all day.
  if (pathname === "/reception" || pathname.startsWith("/reception/")) {
    return "reception_queue";
  }
  // One appointment's detail screen: /appointments/<uuid>. NOT the calendar, the day view, the
  // list or the create form — those are `/appointments`, `/appointments/day`,
  // `/appointments/list` and `/appointments/new`, and calling them "appointment_detail" would put
  // a wrong answer in the audit log, which is worse than no answer.
  if (/^\/appointments\/(?!day$|list$|new$)[^/]+$/.test(pathname)) {
    return "appointment_detail";
  }
  return null;
}
const ACCESS_KEY = "addy.accessToken";
const REFRESH_KEY = "addy.refreshToken";
const TENANT_KEY = "addy.tenantId";

const baseURL =
  (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? "/api";

/**
 * Silent refresh. When the 1-day access token expires, exchange the (14-day)
 * refresh token for a fresh access token and transparently retry the failed
 * request, so the user is never bounced to /login mid-session. Concurrent 401s
 * share a single in-flight refresh. A bare axios instance is used to avoid the
 * interceptor recursing on itself.
 */
let refreshInFlight: Promise<string | null> | null = null;

async function doRefresh(): Promise<string | null> {
  const refreshToken = localStorage.getItem(REFRESH_KEY);
  if (!refreshToken) return null;
  const tenantId = localStorage.getItem(TENANT_KEY);
  try {
    const res = await axios.post(
      `${baseURL}/v1/auth/refresh`,
      { refreshToken },
      { timeout: 20_000, headers: tenantId ? { [TENANT_HEADER]: tenantId } : {} }
    );
    const d = res.data as {
      accessToken?: string;
      refreshToken?: string;
      tenantId?: string;
    };
    if (!d?.accessToken) return null;
    localStorage.setItem(ACCESS_KEY, d.accessToken);
    if (d.refreshToken) localStorage.setItem(REFRESH_KEY, d.refreshToken);
    if (d.tenantId) localStorage.setItem(TENANT_KEY, d.tenantId);
    return d.accessToken;
  } catch {
    return null;
  }
}

function refreshOnce(): Promise<string | null> {
  if (!refreshInFlight) {
    refreshInFlight = doRefresh().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

export const apiClient: AxiosInstance = axios.create({
  baseURL,
  timeout: 30_000,
  withCredentials: false,
});

apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem("addy.accessToken");
  if (token) {
    config.headers.set("Authorization", `Bearer ${token}`);
  }
  const tenantId = localStorage.getItem("addy.tenantId");
  if (tenantId) {
    config.headers.set(TENANT_HEADER, tenantId);
  }
  // Active branch (Pro multi-branch). Absent → backend uses the primary branch.
  const branchId = localStorage.getItem("addy.branchId");
  if (branchId) {
    config.headers.set("X-Addy-Branch-Id", branchId);
  }
  config.headers.set("Accept", "application/json");
  // #19239. Not set when the route maps to nothing: the backend then records `api`, which is the
  // correct answer for "an operation from a screen we have not named".
  const source = auditSourceForPath(window.location.pathname);
  if (source) {
    config.headers.set(SOURCE_HEADER, source);
  }
  return config;
});

apiClient.interceptors.response.use(
  (r) => r,
  async (error: AxiosError) => {
    const original = error.config as
      | (InternalAxiosRequestConfig & { _retried?: boolean })
      | undefined;
    const status = error.response?.status;
    const url = original?.url ?? "";
    const isAuthCall = url.includes("/auth/"); // login / refresh / mfa — never refresh-retry these

    // 1) Access token expired → try a silent refresh, then replay the request once.
    if (
      status === 401 &&
      original &&
      !original._retried &&
      !isAuthCall &&
      localStorage.getItem(REFRESH_KEY)
    ) {
      original._retried = true;
      const newToken = await refreshOnce();
      if (newToken) {
        original.headers.set("Authorization", `Bearer ${newToken}`);
        return apiClient(original);
      }
    }

    // 2) No refresh possible / refresh failed → clear session and go to login.
    if (status === 401) {
      localStorage.removeItem(ACCESS_KEY);
      localStorage.removeItem(REFRESH_KEY);
      // Drop the active branch too — it belongs to the previous user/tenant and must
      // not leak into the next login (otherwise the next user hits branch_forbidden).
      localStorage.removeItem("addy.branchId");
      const path = window.location.pathname;
      if (!path.startsWith("/login") && !path.startsWith("/forgot-password")) {
        window.location.assign("/login");
      }
    }

    // 3) Stored active branch is stale/invalid for this user (e.g. left over from a
    //    different tenant on this origin) → the backend answers 403 branch_forbidden on
    //    every request and the app gets stuck. Drop the pinned branch and reload once so
    //    requests fall back to the caller's primary branch.
    // The branch check lives in a servlet filter (TenantResolutionFilter), which writes a
    // hand-rolled problem body {type,title,status,detail} — the error code is in `title`,
    // NOT `code` (that field is only added by the Spring MVC GlobalExceptionHandler). Match
    // both so this survives if the error ever moves layers.
    {
      const body = error.response?.data as
        | { code?: string; title?: string }
        | undefined;
      const isBranchForbidden =
        body?.code === "branch_forbidden" || body?.title === "branch_forbidden";
      if (status === 403 && isBranchForbidden && localStorage.getItem("addy.branchId")) {
        localStorage.removeItem("addy.branchId");
        window.location.reload();
      }
    }
    return Promise.reject(error);
  }
);

/**
 * RFC 7807 problem-details body shape returned by the backend's
 * GlobalExceptionHandler. Plus `properties` for the merged custom fields
 * (e.g. duplicate-warning payload).
 */
export interface ApiProblem {
  type?: string;
  title?: string;
  status?: number;
  detail?: string;
  code?: string;
  // Custom fields merged into the body (e.g. duplicate `warning`)
  [k: string]: unknown;
}

/**
 * Convert any thrown error from an `apiClient.*` call into a stable shape
 * with the Vietnamese fallback message ready for UI display.
 */
export function apiError(error: unknown): {
  status: number;
  code: string;
  title: string;
  detail: string;
  problem: ApiProblem | null;
} {
  if (axios.isAxiosError(error)) {
    const status = error.response?.status ?? 0;
    const body = (error.response?.data ?? null) as ApiProblem | null;
    const code = body?.code ?? `http_${status || "unknown"}`;
    const title = body?.title ?? defaultTitle(status);
    // Bean-validation 400s (code=validation_failed) carry a `violations` array whose per-field
    // `message` is the human (Vietnamese) reason — surface those instead of the generic English
    // `detail` ("Request validation failed") so the user sees e.g. "Nhịp thở phải trong khoảng
    // 5–60 lần/phút." (#18594).
    const violations = Array.isArray(body?.violations)
      ? (body!.violations as Array<{ message?: string }>)
      : null;
    const violationMsg =
      violations
        ?.map((v) => v.message)
        .filter((m): m is string => Boolean(m))
        .join("; ") || null;
    const detail =
      violationMsg ?? body?.detail ?? error.message ?? defaultTitle(status);
    return { status, code, title, detail, problem: body };
  }
  return {
    status: 0,
    code: "unknown",
    title: "Lỗi không xác định",
    detail: error instanceof Error ? error.message : String(error),
    problem: null,
  };
}

function defaultTitle(status: number): string {
  if (status === 0) return "Không kết nối được đến máy chủ";
  if (status === 401) return "Phiên đăng nhập đã hết hạn";
  if (status === 403) return "Bạn không có quyền thao tác này";
  if (status === 404) return "Không tìm thấy dữ liệu";
  if (status === 409) return "Xung đột dữ liệu";
  if (status === 422) return "Dữ liệu không hợp lệ";
  if (status === 402) return "Tính năng dành cho gói Pro";
  if (status >= 500) return "Máy chủ đang gặp lỗi, vui lòng thử lại";
  return "Đã xảy ra lỗi";
}
